import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Layouts, Modal, NoData } from 'components'
import { head } from 'helpers'
import s from './styles.module.scss'
import * as api from 'api'

const TABS = [
  { id: 'purohit', label: 'Purohit on Call', icon: 'support_agent', description: 'Register and manage purohits available on call.' },
  { id: 'mandir', label: 'Mandir Form', icon: 'account_balance', description: 'Register mandir details and associated purohits.' },
  { id: 'streams', label: 'Spirituality Data', icon: 'live_tv', description: 'Manage temple live stream links shown on the site.' },
]

const educationOptions = [
  'Gurukul Trained',
  'Shastri',
  'Acharya',
  'Ved Pathi',
  'Family Tradition',
]

const serviceOptions = [
  'Daily Puja',
  'Satyanarayan Katha',
  'Vivah Sanskar',
  'Grih Pravesh',
  'Bhoomi Pujan',
  'Shraddh / Pind Daan',
  'Grah Shanti / Navgrah Puja',
  'Havan / Yajna',
  'Astrology / Kundali',
]

const landOwnershipOptions = ['Private', 'Trust', 'Govt. Land', 'Panchayat', 'Other']

const mandirEducationOptions = [
  { value: 'gurukul', label: 'Gurukul Trained (Specify Gurukul / Acharya)' },
  { value: 'shastri', label: 'Shastri (Graduate in Sanskrit Studies)' },
  { value: 'acharya', label: 'Acharya (Post Graduate in Sanskrit Studies)' },
  { value: 'vedPathi', label: 'Ved Pathi / Vedadhyayi (Rigveda / Yajurveda / Samaveda / Atharvaveda)' },
  { value: 'karmakand', label: 'Purohit Training / Karmakand Training (Specify Institution)' },
  { value: 'jyotish', label: 'Jyotishacharya / Vastu Shastra Trained' },
  { value: 'karamkandi', label: 'Karamkandi (Specialized in Sanskar / Yagna / Puja Rituals)' },
  { value: 'familyTradition', label: 'Self-Trained / Family Tradition (Guru Parampara)' },
  { value: 'other', label: 'Other (Specify)' },
]

const blankMandirAdditionalPurohit = { name: '', mobile: '', dob: '', education: '' }

const blankReference = { name: '', mobile: '', relation: '', knownYears: '' }

const defaultPurohitForm = {
  fullName: '',
  fatherName: '',
  dob: '',
  mobile: '',
  whatsapp: '',
  address: '',
  city: '',
  guruName: '',
  specialisationInput: '',
  experienceYears: '',
  otherSpecialization: '',
  mandirName: '',
  mandirAddress: '',
  mandirState: '',
  mandirCity: '',
}

const defaultMandirForm = {
  mandirName: '',
  mandirDescription: '',
  mandirAddress: '',
  mandirState: '',
  mandirCity: '',
  mandirPinCode: '',
  yearOfEstablishment: '',
  landOwnershipType: '',
  registrationNumber: '',
  managingAuthority: '',
  trusteeMobile: '',
  remarks: '',
  purohitName: '',
  purohitFatherName: '',
  purohitDob: '',
  purohitMobile: '',
  educationSelections: [],
  educationDetails: {
    gurukul: '',
    karmakand: '',
    other: '',
  },
  specialisationInput: '',
  yearsOfService: '',
  otherPurohits: [{ ...blankMandirAdditionalPurohit }],
}

const defaultTempleForm = { name: '', details: '', streamUrl: '' }

const formatDateInput = value => {
  if (!value) return ''
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toISOString().split('T')[0]
  } catch {
    return ''
  }
}

export default function SpiritualityAdmin() {
  const [activeTab, setActiveTab] = useState('purohit')

  // Purohit on Call state
  const [purohitForm, setPurohitForm] = useState(defaultPurohitForm)
  const [educationSelection, setEducationSelection] = useState([])
  const [serviceSelection, setServiceSelection] = useState([])
  const [references, setReferences] = useState([{ ...blankReference }])
  const [profilePhoto, setProfilePhoto] = useState(null)
  const [referencePhotos, setReferencePhotos] = useState([])
  const [purohitLoading, setPurohitLoading] = useState(false)
  const [purohitList, setPurohitList] = useState([])

  // Mandir registration state
  const [mandirForm, setMandirForm] = useState(defaultMandirForm)
  const [mandirPhotos, setMandirPhotos] = useState([])
  const [mandirPurohitPhoto, setMandirPurohitPhoto] = useState(null)
  const [mandirList, setMandirList] = useState([])
  const [mandirSaving, setMandirSaving] = useState(false)
  const [editingMandirId, setEditingMandirId] = useState(null)

  // Spirituality streams state
  const [templeForm, setTempleForm] = useState(defaultTempleForm)
  const [templeStreams, setTempleStreams] = useState([])
  const [templeSaving, setTempleSaving] = useState(false)
  const [editingTempleId, setEditingTempleId] = useState(null)

  useEffect(() => {
    head({ title: 'Spirituality | BCN' })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const storedUser = JSON.parse(window.localStorage.getItem('userData') || '{}')
      if (storedUser?.state || storedUser?.city) {
        setMandirForm(prev => ({
          ...prev,
          mandirState: prev.mandirState || storedUser.state || '',
          mandirCity: prev.mandirCity || storedUser.city || '',
        }))
      }
    } catch (error) {
      console.warn('Unable to prefill mandir location details', error)
    }
  }, [])

  const specialisationTags = useMemo(() =>
    purohitForm.specialisationInput
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean),
  [purohitForm.specialisationInput])

  const mandirSpecialisationTags = useMemo(
    () =>
      mandirForm.specialisationInput
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean),
    [mandirForm.specialisationInput]
  )

  const activeTabMeta = useMemo(
    () => TABS.find(tab => tab.id === activeTab) || TABS[0],
    [activeTab]
  )

  const stats = useMemo(
    () => ({
      purohits: purohitList.length,
      mandirs: mandirList.length,
      streams: templeStreams.length,
    }),
    [purohitList.length, mandirList.length, templeStreams.length]
  )

  const fetchPurohits = useCallback(async () => {
    try {
      const response = await api.admin.purohitOnCall.fetchAll()
      if (response?.code === 200 && Array.isArray(response.payload)) {
        setPurohitList(response.payload)
      } else {
        Alert.error(response?.message || 'Failed to load Purohit records.')
      }
    } catch (error) {
      console.error('Error fetching Purohit on Call data:', error)
      Alert.error('Failed to load Purohit on Call data.')
    }
  }, [])

  const fetchMandirs = useCallback(async () => {
    try {
      const response = await api.admin.mandir.fetchAll()
      if (response?.code === 200 && Array.isArray(response.payload)) {
        setMandirList(response.payload)
      } else {
        Alert.error(response?.message || 'Failed to load Mandir records.')
      }
    } catch (error) {
      console.error('Error fetching Mandir details:', error)
      Alert.error('Failed to load Mandir details.')
    }
  }, [])

  const fetchStreams = useCallback(async () => {
    try {
      const response = await api.admin.spirituality.fetchAllSpirituality()
      if (response?.code === 200 && Array.isArray(response.payload)) {
        setTempleStreams(response.payload)
      } else {
        Alert.error(response?.message || 'Failed to load streams.')
      }
    } catch (error) {
      console.error('Error fetching streams:', error)
      Alert.error('Failed to load streams.')
    }
  }, [])

  useEffect(() => {
    fetchPurohits()
    fetchMandirs()
    fetchStreams()
  }, [fetchPurohits, fetchMandirs, fetchStreams])

  const handlePurohitInputChange = ({ target }) => {
    const { name, value } = target
    setPurohitForm(prev => ({ ...prev, [name]: value }))
  }

  const toggleSelection = (value, selection, setter) => {
    setter(prev => (prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]))
  }

  const handleReferenceChange = (index, field, value) => {
    setReferences(prev => {
      const updated = [...prev]
      updated[index] = { ...updated[index], [field]: value }
      return updated
    })
  }

  const addReferenceRow = () => setReferences(prev => [...prev, { ...blankReference }])

  const removeReferenceRow = index => {
    setReferences(prev => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)))
  }

  const handleProfilePhotoChange = e => {
    const file = e.target.files?.[0] || null
    setProfilePhoto(file)
  }

  const handleReferencePhotosChange = e => {
    const files = Array.from(e.target.files || []).slice(0, 5)
    setReferencePhotos(files)
  }

  const handlePurohitSubmit = async e => {
    e.preventDefault()

    if (!purohitForm.fullName || !purohitForm.mobile) {
      Alert.warn('Full name and mobile number are required!')
      return
    }

    const cleanedReferences = references
      .filter(ref => ref.name || ref.mobile || ref.relation || ref.knownYears)
      .map(ref => ({
        ...ref,
        knownYears: ref.knownYears ? Number(ref.knownYears) : 0,
      }))

    const mandirAssociation = {
      name: purohitForm.mandirName,
      address: purohitForm.mandirAddress,
      state: purohitForm.mandirState,
      city: purohitForm.mandirCity,
    }

    const formData = new FormData()
    Object.entries({
      fullName: purohitForm.fullName,
      fatherName: purohitForm.fatherName,
      dob: purohitForm.dob,
      mobile: purohitForm.mobile,
      whatsapp: purohitForm.whatsapp,
      address: purohitForm.address,
      city: purohitForm.city,
      guruName: purohitForm.guruName,
      experienceYears: purohitForm.experienceYears,
    }).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })

    formData.append('religiousEducation', JSON.stringify(educationSelection))
    formData.append('specialisationTags', JSON.stringify(specialisationTags))
    formData.append('serviceSpecializations', JSON.stringify(serviceSelection))
    formData.append('references', JSON.stringify(cleanedReferences))
    formData.append('mandirAssociation', JSON.stringify(mandirAssociation))
    formData.append('otherSpecialization', purohitForm.otherSpecialization || '')
    formData.append('isApproved', 'true')

    if (profilePhoto) {
      formData.append('profilePhoto', profilePhoto)
    }

    referencePhotos.forEach(file => {
      formData.append('referencePhotos', file)
    })

    setPurohitLoading(true)
    Alert.success('Saving Purohit profile...')

    try {
      const response = await api.admin.purohitOnCall.create(formData)
      if (response?.code === 201 && response.payload) {
        Alert.success('Purohit profile saved successfully!')
        setPurohitList(prev => [response.payload, ...prev])
        setPurohitForm({ ...defaultPurohitForm })
        setEducationSelection([])
        setServiceSelection([])
        setReferences([{ ...blankReference }])
        setProfilePhoto(null)
        setReferencePhotos([])
        setActiveTab('purohit')
      } else {
        Alert.error(response?.message || 'Failed to save profile.')
      }
    } catch (error) {
      console.error('Error saving Purohit profile:', error)
      Alert.error('Failed to save Purohit profile.')
    } finally {
      setPurohitLoading(false)
    }
  }

  const handlePurohitDelete = id => {
    Modal.Confirm('Are you sure you want to delete this entry?', async () => {
      try {
        const response = await api.admin.purohitOnCall.delete(id)
        if (response?.code === 200) {
          setPurohitList(prev => prev.filter(item => item._id !== id))
          Alert.success('Entry deleted successfully!')
        } else {
          Alert.error(response?.message || 'Failed to delete entry.')
        }
      } catch (error) {
        console.error('Error deleting Purohit entry:', error)
        Alert.error('Failed to delete entry.')
      }
    })
  }

  const handleMandirInputChange = ({ target }) => {
    const { name, value } = target
    setMandirForm(prev => ({ ...prev, [name]: value }))
  }

  const handleMandirEducationToggle = value => {
    setMandirForm(prev => {
      const alreadySelected = prev.educationSelections.includes(value)
      const updatedSelections = alreadySelected
        ? prev.educationSelections.filter(item => item !== value)
        : [...prev.educationSelections, value]

      const detailKeyMap = {
        gurukul: 'gurukul',
        karmakand: 'karmakand',
        other: 'other',
      }

      let updatedDetails = prev.educationDetails
      if (alreadySelected && detailKeyMap[value]) {
        updatedDetails = { ...updatedDetails, [detailKeyMap[value]]: '' }
      }

      return {
        ...prev,
        educationSelections: updatedSelections,
        educationDetails: updatedDetails,
      }
    })
  }

  const handleMandirEducationDetailChange = (key, value) => {
    setMandirForm(prev => ({
      ...prev,
      educationDetails: { ...prev.educationDetails, [key]: value },
    }))
  }

  const handleMandirOtherPurohitChange = (index, field, value) => {
    setMandirForm(prev => {
      const updated = prev.otherPurohits.map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item
      )
      return { ...prev, otherPurohits: updated }
    })
  }

  const addMandirOtherPurohit = () => {
    setMandirForm(prev => ({
      ...prev,
      otherPurohits: [...prev.otherPurohits, { ...blankMandirAdditionalPurohit }],
    }))
  }

  const removeMandirOtherPurohit = index => {
    setMandirForm(prev => {
      if (prev.otherPurohits.length === 1) return prev
      return {
        ...prev,
        otherPurohits: prev.otherPurohits.filter((_, idx) => idx !== index),
      }
    })
  }

  const handleMandirPhotosChange = event => {
    const files = Array.from(event.target.files || []).slice(0, 5)
    setMandirPhotos(files)
  }

  const handleMandirPurohitPhotoChange = event => {
    const file = event.target.files?.[0] || null
    setMandirPurohitPhoto(file)
  }

  const resetMandirForm = (previousState = {}) =>
    setMandirForm({
      ...defaultMandirForm,
      mandirState: previousState.mandirState || defaultMandirForm.mandirState,
      mandirCity: previousState.mandirCity || defaultMandirForm.mandirCity,
    })

  const handleMandirEdit = mandir => {
    setMandirForm({
      mandirName: mandir.mandirName || '',
      mandirDescription: mandir.description || '',
      mandirAddress: mandir.address || '',
      mandirState: mandir.state || '',
      mandirCity: mandir.city || '',
      mandirPinCode: mandir.pinCode || '',
      yearOfEstablishment: mandir.yearOfEstablishment ? String(mandir.yearOfEstablishment) : '',
      landOwnershipType: mandir.landOwnershipType || '',
      registrationNumber: mandir.registrationNumber || '',
      managingAuthority: mandir.managingAuthority || '',
      trusteeMobile: mandir.trusteeMobile || '',
      remarks: mandir.remarks || '',
      purohitName: mandir.purohit?.fullName || '',
      purohitFatherName: mandir.purohit?.fatherName || '',
      purohitDob: formatDateInput(mandir.purohit?.dob),
      purohitMobile: mandir.purohit?.mobile || '',
      educationSelections: mandir.purohit?.educationSelections || [],
      educationDetails: {
        gurukul: mandir.purohit?.educationDetails?.gurukul || '',
        karmakand: mandir.purohit?.educationDetails?.karmakand || '',
        other: mandir.purohit?.educationDetails?.other || '',
      },
      specialisationInput: (mandir.purohit?.specialisationTags || []).join(', '),
      yearsOfService: mandir.purohit?.yearsOfService ? String(mandir.purohit.yearsOfService) : '',
      otherPurohits:
        mandir.otherPurohits?.length > 0
          ? mandir.otherPurohits.map(p => ({
              name: p.name || '',
              mobile: p.mobile || '',
              dob: formatDateInput(p.dob),
              education: p.education || '',
            }))
          : [{ ...blankMandirAdditionalPurohit }],
    })
    setMandirPhotos([])
    setMandirPurohitPhoto(null)
    setEditingMandirId(mandir._id)
    setActiveTab('mandir')
  }

  const handleMandirDelete = mandir => {
    Modal.Confirm('Are you sure you want to delete this Mandir entry?', async () => {
      try {
        const response = await api.admin.mandir.delete(mandir._id)
        if (response?.code === 200) {
          setMandirList(prev => prev.filter(item => item._id !== mandir._id))
          Alert.success('Mandir entry deleted successfully!')
        } else {
          Alert.error(response?.message || 'Failed to delete Mandir entry.')
        }
      } catch (error) {
        console.error('Error deleting Mandir entry:', error)
        Alert.error('Failed to delete Mandir entry.')
      }
    })
  }

  const handleMandirSubmit = async event => {
    event.preventDefault()

    if (!mandirForm.mandirName.trim()) {
      Alert.warn('Mandir name is required.')
      return
    }

    if (!mandirForm.mandirAddress.trim()) {
      Alert.warn('Mandir address is required.')
      return
    }

    if (!mandirForm.mandirState.trim() || !mandirForm.mandirCity.trim()) {
      Alert.warn('State and City are required for Mandir details.')
      return
    }

    if (!mandirForm.purohitName.trim() || !mandirForm.purohitMobile.trim()) {
      Alert.warn('Main Purohit name and mobile number are required.')
      return
    }

    const currentYear = new Date().getFullYear()
    if (mandirForm.yearOfEstablishment) {
      const year = Number(mandirForm.yearOfEstablishment)
      if (Number.isNaN(year) || year < 1000 || year > currentYear) {
        Alert.warn('Please enter a valid Year of Establishment.')
        return
      }
    }

    const cleanedOtherPurohits = mandirForm.otherPurohits
      .filter(item => item.name || item.mobile || item.dob || item.education)
      .map(item => ({
        ...item,
        dob: item.dob || null,
      }))

    const formData = new FormData()
    formData.append('mandirName', mandirForm.mandirName.trim())
    formData.append('description', mandirForm.mandirDescription.trim().slice(0, 1000))
    formData.append('address', mandirForm.mandirAddress.trim())
    formData.append('state', mandirForm.mandirState.trim())
    formData.append('city', mandirForm.mandirCity.trim())
    formData.append('pinCode', mandirForm.mandirPinCode.trim())
    formData.append('yearOfEstablishment', mandirForm.yearOfEstablishment.trim())
    formData.append('landOwnershipType', mandirForm.landOwnershipType)
    formData.append('registrationNumber', mandirForm.registrationNumber.trim())
    formData.append('managingAuthority', mandirForm.managingAuthority.trim())
    formData.append('trusteeMobile', mandirForm.trusteeMobile.trim())
    formData.append('remarks', mandirForm.remarks.trim().slice(0, 1000))
    formData.append('purohitName', mandirForm.purohitName.trim())
    formData.append('purohitFatherName', mandirForm.purohitFatherName.trim())
    formData.append('purohitDob', mandirForm.purohitDob)
    formData.append('purohitMobile', mandirForm.purohitMobile.trim())
    formData.append('yearsOfService', mandirForm.yearsOfService.trim())
    formData.append('educationSelections', JSON.stringify(mandirForm.educationSelections))
    formData.append(
      'educationDetails',
      JSON.stringify({
        gurukul: mandirForm.educationDetails.gurukul?.slice(0, 300) || '',
        karmakand: mandirForm.educationDetails.karmakand?.slice(0, 300) || '',
        other: mandirForm.educationDetails.other?.slice(0, 300) || '',
      })
    )
    formData.append('specialisationTags', JSON.stringify(mandirSpecialisationTags))
    formData.append('otherPurohits', JSON.stringify(cleanedOtherPurohits))

    mandirPhotos.forEach(file => {
      formData.append('mandirPhotos', file)
    })

    if (mandirPurohitPhoto) {
      formData.append('purohitPhoto', mandirPurohitPhoto)
    }

    setMandirSaving(true)
    Alert.success(editingMandirId ? 'Updating Mandir details...' : 'Saving Mandir details...')

    try {
      let response

      if (editingMandirId) {
        formData.append('id', editingMandirId)
        response = await api.admin.mandir.update(formData)
      } else {
        response = await api.admin.mandir.create(formData)
      }

      if (response?.code === 200 || response?.code === 201) {
        const payload = response.payload
        if (editingMandirId) {
          setMandirList(prev => prev.map(item => (item._id === editingMandirId ? payload : item)))
          Alert.success('Mandir details updated successfully!')
        } else {
          setMandirList(prev => [payload, ...prev])
          Alert.success('Mandir details saved successfully!')
        }

        const snapshot = { mandirState: mandirForm.mandirState, mandirCity: mandirForm.mandirCity }
        resetMandirForm(snapshot)
        setMandirPhotos([])
        setMandirPurohitPhoto(null)
        setEditingMandirId(null)
        fetchMandirs()
      } else {
        Alert.error(response?.message || 'Failed to save Mandir details.')
      }
    } catch (error) {
      console.error('Error saving Mandir details:', error)
      Alert.error('Failed to save Mandir details.')
    } finally {
      setMandirSaving(false)
    }
  }

  const handleTempleChange = ({ target }) => {
    const { name, value } = target
    setTempleForm(prev => ({ ...prev, [name]: value }))
  }

  const handleTempleSubmit = async e => {
    e.preventDefault()
  
    Modal.Confirm(
      editingTempleId ? 'Are you sure you want to update this temple?' : 'Are you sure you want to save temple details?',
      async () => {
        setTempleSaving(true)
        Alert.success(editingTempleId ? 'Updating temple details...' : 'Saving temple details...')
  
        try {
          let response
  
          if (editingTempleId) {
            response = await api.admin.spirituality.updateSpirituality({
              id: editingTempleId,
              ...templeForm,
            })

            if (response?.code === 200) {
              Alert.success('Temple updated successfully!')
              setTempleStreams(prev =>
                prev.map(stream => (stream._id === editingTempleId ? { ...stream, ...templeForm } : stream))
              )
            }
          } else {
            response = await api.admin.spirituality.addSpirituality(templeForm)

            if (response?.code === 201 && response.payload?.spirituality) {
              Alert.success('Temple added successfully!')
              setTempleStreams(prev => [...prev, response.payload.spirituality])
            }
          }

          if (response?.code !== 200 && response?.code !== 201) {
            Alert.error(response?.message || 'An error occurred.')
          } else {
            setTempleForm(defaultTempleForm)
            setEditingTempleId(null)
          }
        } catch (error) {
          console.error('Error saving temple:', error)
          Alert.error('An error occurred while saving.')
        } finally {
          setTempleSaving(false)
        }
      }
    )
  }

  const handleTempleEdit = stream => {
    setTempleForm({
      name: stream.name,
      details: stream.details,
      streamUrl: stream.streamUrl,
    })
    setEditingTempleId(stream._id)
    setActiveTab('streams')
  }

  const handleTempleDelete = stream => {
    Modal.Confirm('Are you sure you want to delete this temple?', async () => {
      try {
        const response = await api.admin.spirituality.deleteSpirituality(stream._id)
        if (response?.code === 200 || response?.code === 201) {
          setTempleStreams(prev => prev.filter(item => item._id !== stream._id))
          Alert.success('Temple deleted successfully!')
        } else {
          Alert.error(response?.message || 'Failed to delete temple.')
        }
      } catch (error) {
        console.error('Error deleting temple:', error)
        Alert.error('An error occurred while deleting.')
      }
    })
  }

  const renderPurohitTab = () => (
    <div className={s.tabContent}>
      <h3 className={s.sectionTitle}>Register Purohit on Call</h3>
      <form className={s.purohitForm} onSubmit={handlePurohitSubmit}>
        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Full Name *</label>
            <input
              type="text"
              name="fullName"
              value={purohitForm.fullName}
              onChange={handlePurohitInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>Father's Name</label>
            <input
              type="text"
              name="fatherName"
              value={purohitForm.fatherName}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Date of Birth</label>
            <input
              type="date"
              name="dob"
              value={purohitForm.dob}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Mobile *</label>
            <input
              type="tel"
              name="mobile"
              value={purohitForm.mobile}
              onChange={handlePurohitInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>WhatsApp</label>
            <input
              type="tel"
              name="whatsapp"
              value={purohitForm.whatsapp}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Current Address</label>
            <input
              type="text"
              name="address"
              value={purohitForm.address}
              onChange={handlePurohitInputChange}
              placeholder="City / District"
            />
          </div>
          <div className={s.inputGroup}>
            <label>City</label>
            <input
              type="text"
              name="city"
              value={purohitForm.city}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Profile Photo</label>
            <input type="file" accept="image/*" onChange={handleProfilePhotoChange} />
            {profilePhoto ? <p className={s.helperText}>{profilePhoto.name}</p> : null}
          </div>
        </div>

        <div className={s.sectionDivider}>
          <h4>Education & Experience</h4>
        </div>

        <div className={s.checkboxGroup}>
          {educationOptions.map(option => (
            <label key={option}>
              <input
                type="checkbox"
                checked={educationSelection.includes(option)}
                onChange={() => toggleSelection(option, educationSelection, setEducationSelection)}
              />
              {option}
            </label>
          ))}
        </div>

        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Guru / Gurukul Name</label>
            <input
              type="text"
              name="guruName"
              value={purohitForm.guruName}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Specialisation Tags (comma separated)</label>
            <input
              type="text"
              name="specialisationInput"
              value={purohitForm.specialisationInput}
              onChange={handlePurohitInputChange}
              placeholder="e.g. Vastu, Jyotish"
            />
          </div>
          <div className={s.inputGroup}>
            <label>Total Experience (Years)</label>
            <input
              type="number"
              min="0"
              name="experienceYears"
              value={purohitForm.experienceYears}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Reference Photos (max 5)</label>
            <input type="file" accept="image/*" multiple onChange={handleReferencePhotosChange} />
            {referencePhotos.length > 0 ? (
              <p className={s.helperText}>{referencePhotos.length} file(s) selected</p>
            ) : null}
          </div>
        </div>

        <div className={s.sectionDivider}>
          <h4>Puja &amp; Service Specialisation</h4>
        </div>

        <div className={s.checkboxGroup}>
          {serviceOptions.map(option => (
            <label key={option}>
              <input
                type="checkbox"
                checked={serviceSelection.includes(option)}
                onChange={() => toggleSelection(option, serviceSelection, setServiceSelection)}
              />
              {option}
            </label>
          ))}
          <label>
            <input
              type="checkbox"
              checked={serviceSelection.includes('Other')}
              onChange={() => toggleSelection('Other', serviceSelection, setServiceSelection)}
            />
            Other services
          </label>
        </div>

        {serviceSelection.includes('Other') && (
          <div className={s.inputGroup}>
            <label>Please specify other services</label>
            <input
              type="text"
              name="otherSpecialization"
              value={purohitForm.otherSpecialization}
              onChange={handlePurohitInputChange}
              placeholder="e.g. Vastu Shanti, Bhagwat Katha"
            />
          </div>
        )}

        <div className={s.sectionDivider}>
          <h4>Mandir / Math Association</h4>
        </div>

        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Mandir Name</label>
            <input
              type="text"
              name="mandirName"
              value={purohitForm.mandirName}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Address</label>
            <input
              type="text"
              name="mandirAddress"
              value={purohitForm.mandirAddress}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>State</label>
            <input
              type="text"
              name="mandirState"
              value={purohitForm.mandirState}
              onChange={handlePurohitInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>City</label>
            <input
              type="text"
              name="mandirCity"
              value={purohitForm.mandirCity}
              onChange={handlePurohitInputChange}
            />
          </div>
        </div>

        <div className={s.sectionDivider}>
          <h4>Reference of Work (Yajman)</h4>
        </div>

        <div className={s.referenceList}>
          {references.map((ref, index) => (
            <div key={index} className={s.referenceRow}>
              <input
                type="text"
                placeholder="Name"
                value={ref.name}
                onChange={e => handleReferenceChange(index, 'name', e.target.value)}
              />
              <input
                type="tel"
                placeholder="Mobile"
                value={ref.mobile}
                onChange={e => handleReferenceChange(index, 'mobile', e.target.value)}
              />
              <input
                type="text"
                placeholder="Relation"
                value={ref.relation}
                onChange={e => handleReferenceChange(index, 'relation', e.target.value)}
              />
              <input
                type="number"
                min="0"
                placeholder="Years Known"
                value={ref.knownYears}
                onChange={e => handleReferenceChange(index, 'knownYears', e.target.value)}
              />
              <button type="button" onClick={() => removeReferenceRow(index)} className={s.deleteSmall}>
                Remove
              </button>
            </div>
          ))}
          <button type="button" className={s.addRowButton} onClick={addReferenceRow}>
            + Add Reference
          </button>
        </div>

        <div className={s.actionRow}>
          <button type="submit" className={s.saveButton} disabled={purohitLoading}>
            {purohitLoading ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </form>

      <div className={s.listSection}>
        <div className={s.listHeader}>
          <h3 className={s.sectionTitle}>Registered Purohits</h3>
          <span className={s.countBadge}>{purohitList.length} records</span>
        </div>
        {purohitList.length === 0 ? (
          <div className={s.emptyCard}>
            <NoData />
            <p>No purohit profiles have been registered yet.</p>
          </div>
        ) : (
          <div className={s.tableWrapper}>
            <table className={s.listTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Experience</th>
                  <th>Education</th>
                  <th>Services</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {purohitList.map(entry => (
                  <tr key={entry._id}>
                    <td>{entry.fullName}</td>
                    <td>{entry.mobile}</td>
                    <td>{entry.experienceYears || 0} yrs</td>
                    <td>{(entry.religiousEducation || []).join(', ') || '-'}</td>
                    <td>{(entry.serviceSpecializations || []).join(', ') || '-'}</td>
                    <td>
                      <button
                        type="button"
                        className={s.deleteButton}
                        onClick={() => handlePurohitDelete(entry._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )

  const renderMandirTab = () => (
    <div className={s.tabContent}>
      <form className={s.mandirForm} onSubmit={handleMandirSubmit}>
        <h3 className={s.sectionTitle}>{editingMandirId ? 'Update Mandir Details' : 'Register Mandir'}</h3>

        <div className={s.sectionDivider}>
          <h4>Section A – Mandir Details</h4>
        </div>

        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Mandir Name (as per signage / local use)</label>
            <input
              type="text"
              name="mandirName"
              value={mandirForm.mandirName}
              onChange={handleMandirInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>Mandir Address</label>
            <input
              type="text"
              name="mandirAddress"
              value={mandirForm.mandirAddress}
              onChange={handleMandirInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>State</label>
            <input
              type="text"
              name="mandirState"
              value={mandirForm.mandirState}
              onChange={handleMandirInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>City</label>
            <input
              type="text"
              name="mandirCity"
              value={mandirForm.mandirCity}
              onChange={handleMandirInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>Pin Code</label>
            <input
              type="text"
              name="mandirPinCode"
              value={mandirForm.mandirPinCode}
              onChange={handleMandirInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Year of Establishment</label>
            <input
              type="number"
              min="1000"
              max={new Date().getFullYear()}
              name="yearOfEstablishment"
              value={mandirForm.yearOfEstablishment}
              onChange={handleMandirInputChange}
              placeholder="e.g. 1985"
            />
          </div>
          <div className={s.inputGroup}>
            <label>Land Ownership Type</label>
            <select
              name="landOwnershipType"
              value={mandirForm.landOwnershipType}
              onChange={handleMandirInputChange}
            >
              <option value="">Select ownership</option>
              {landOwnershipOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className={s.inputGroup}>
            <label>Mandir Registration Number</label>
            <input
              type="text"
              name="registrationNumber"
              value={mandirForm.registrationNumber}
              onChange={handleMandirInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Managing Authority / Trust Name</label>
            <input
              type="text"
              name="managingAuthority"
              value={mandirForm.managingAuthority}
              onChange={handleMandirInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Trustee Mobile</label>
            <input
              type="tel"
              name="trusteeMobile"
              value={mandirForm.trusteeMobile}
              onChange={handleMandirInputChange}
            />
          </div>
        </div>

        <div className={s.inputGroup}>
          <label>Mandir Description (Popular for, History etc)</label>
          <textarea
            name="mandirDescription"
            rows={3}
            value={mandirForm.mandirDescription}
            onChange={handleMandirInputChange}
          />
        </div>

        <div className={s.inputGroup}>
          <label>Remarks</label>
          <textarea
            name="remarks"
            rows={3}
            value={mandirForm.remarks}
            onChange={handleMandirInputChange}
          />
        </div>

        <div className={s.inputGroup}>
          <label>Mandir Photos (max 5)</label>
          <input type="file" accept="image/*" multiple onChange={handleMandirPhotosChange} />
          {mandirPhotos.length > 0 ? (
            <span className={s.fileInfo}>{mandirPhotos.length} file(s) selected</span>
          ) : null}
        </div>

        <div className={s.sectionDivider}>
          <h4>Section B – Main Purohit / Priest Details</h4>
        </div>

        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Full Name</label>
            <input
              type="text"
              name="purohitName"
              value={mandirForm.purohitName}
              onChange={handleMandirInputChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>Father’s Name</label>
            <input
              type="text"
              name="purohitFatherName"
              value={mandirForm.purohitFatherName}
              onChange={handleMandirInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Date of Birth</label>
            <input
              type="date"
              name="purohitDob"
              value={mandirForm.purohitDob}
              onChange={handleMandirInputChange}
            />
          </div>
          <div className={s.inputGroup}>
            <label>Mobile Number</label>
            <input
              type="tel"
              name="purohitMobile"
              value={mandirForm.purohitMobile}
              onChange={handleMandirInputChange}
              required
            />
          </div>
        </div>

        <div className={s.checkboxList}>
          {mandirEducationOptions.map(option => (
            <label key={option.value}>
              <input
                type="checkbox"
                checked={mandirForm.educationSelections.includes(option.value)}
                onChange={() => handleMandirEducationToggle(option.value)}
              />
              {option.label}
            </label>
          ))}
        </div>

        {mandirForm.educationSelections.includes('gurukul') ? (
          <div className={s.inputGroup}>
            <label>Gurukul / Acharya Name</label>
            <input
              type="text"
              value={mandirForm.educationDetails.gurukul}
              onChange={event => handleMandirEducationDetailChange('gurukul', event.target.value)}
              placeholder="Specify Gurukul / Acharya"
            />
          </div>
        ) : null}

        {mandirForm.educationSelections.includes('karmakand') ? (
          <div className={s.inputGroup}>
            <label>Karmakand Training Institution</label>
            <input
              type="text"
              value={mandirForm.educationDetails.karmakand}
              onChange={event => handleMandirEducationDetailChange('karmakand', event.target.value)}
              placeholder="Specify Institution"
            />
          </div>
        ) : null}

        {mandirForm.educationSelections.includes('other') ? (
          <div className={s.inputGroup}>
            <label>Other Religious / Sanskrit Education</label>
            <input
              type="text"
              value={mandirForm.educationDetails.other}
              onChange={event => handleMandirEducationDetailChange('other', event.target.value)}
              placeholder="Specify other education details"
            />
          </div>
        ) : null}

        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Specialisation Area (tag searchable)</label>
            <input
              type="text"
              name="specialisationInput"
              value={mandirForm.specialisationInput}
              onChange={handleMandirInputChange}
              placeholder="e.g. Navgrah Puja, Vastu"
            />
          </div>
          <div className={s.inputGroup}>
            <label>Purohit Photo</label>
            <input type="file" accept="image/*" onChange={handleMandirPurohitPhotoChange} />
            {mandirPurohitPhoto ? <span className={s.fileInfo}>{mandirPurohitPhoto.name}</span> : null}
          </div>
          <div className={s.inputGroup}>
            <label>Years of Service in Mandir</label>
            <input
              type="number"
              min="0"
              name="yearsOfService"
              value={mandirForm.yearsOfService}
              onChange={handleMandirInputChange}
              placeholder="e.g. 12"
            />
          </div>
        </div>

        <div className={s.sectionDivider}>
          <h4>Other Purohit Details</h4>
        </div>

        <div className={s.referenceList}>
          {mandirForm.otherPurohits.map((purohit, index) => (
            <div key={`additional-purohit-${index}`} className={s.referenceRow}>
              <input
                type="text"
                placeholder="Name"
                value={purohit.name}
                onChange={event => handleMandirOtherPurohitChange(index, 'name', event.target.value)}
              />
              <input
                type="tel"
                placeholder="Mobile"
                value={purohit.mobile}
                onChange={event => handleMandirOtherPurohitChange(index, 'mobile', event.target.value)}
              />
              <input
                type="date"
                placeholder="DOB"
                value={purohit.dob}
                onChange={event => handleMandirOtherPurohitChange(index, 'dob', event.target.value)}
              />
              <input
                type="text"
                placeholder="Highest Religious Education"
                value={purohit.education}
                onChange={event => handleMandirOtherPurohitChange(index, 'education', event.target.value)}
              />
              <button
                type="button"
                className={s.chipRemove}
                onClick={() => removeMandirOtherPurohit(index)}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className={s.linkButton} onClick={addMandirOtherPurohit}>
            + Add Another Purohit
          </button>
        </div>

        <div className={s.formActions}>
          <button type="submit" disabled={mandirSaving}>
            {mandirSaving ? (editingMandirId ? 'Updating...' : 'Submitting...') : editingMandirId ? 'Update Mandir' : 'Submit Mandir'}
          </button>
          {editingMandirId ? (
            <button
              type="button"
              className={s.secondaryButton}
              onClick={() => {
                const snapshot = { mandirState: mandirForm.mandirState, mandirCity: mandirForm.mandirCity }
                resetMandirForm(snapshot)
                setMandirPhotos([])
                setMandirPurohitPhoto(null)
                setEditingMandirId(null)
              }}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </form>

      <div className={s.listSection}>
        <div className={s.listHeader}>
          <h3 className={s.sectionTitle}>Saved Mandir Records</h3>
          <span className={s.countBadge}>{mandirList.length} records</span>
        </div>
        {mandirList.length === 0 ? (
          <div className={s.emptyCard}>
            <NoData />
            <p>No mandir records have been saved yet.</p>
          </div>
        ) : (
          <div className={s.tableWrapper}>
            <table className={s.listTable}>
              <thead>
                <tr>
                  <th>Mandir Name</th>
                  <th>Location</th>
                  <th>Main Purohit</th>
                  <th>Mobile</th>
                  <th>Years of Service</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {mandirList.map(entry => (
                  <tr key={entry._id}>
                    <td>{entry.mandirName}</td>
                    <td>
                      {[entry.city, entry.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td>{entry.purohit?.fullName || '—'}</td>
                    <td>{entry.purohit?.mobile || '—'}</td>
                    <td>{entry.purohit?.yearsOfService ? `${entry.purohit.yearsOfService} yrs` : '—'}</td>
                    <td>
                      <div className={s.tableActions}>
                        <button type="button" className={s.updateButton} onClick={() => handleMandirEdit(entry)}>
                          Edit
                        </button>
                        <button type="button" className={s.deleteButton} onClick={() => handleMandirDelete(entry)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )

  const renderStreamsTab = () => (
    <div className={s.tabContent}>
      <h3 className={s.sectionTitle}>{editingTempleId ? 'Update Temple Stream' : 'Add Temple Stream'}</h3>
      <form onSubmit={handleTempleSubmit} className={s.templeForm}>
        <div className={s.formGrid}>
          <div className={s.inputGroup}>
            <label>Temple Name</label>
        <input
          type="text"
          name="name"
              value={templeForm.name}
              onChange={handleTempleChange}
              required
            />
          </div>
          <div className={s.inputGroup}>
            <label>Live Stream URL</label>
            <input
              type="text"
              name="streamUrl"
              value={templeForm.streamUrl}
              onChange={handleTempleChange}
          required
        />
          </div>
        </div>
        <label>Details</label>
        <textarea
          name="details"
          value={templeForm.details}
          onChange={handleTempleChange}
          required
        />
        <div className={s.actionRow}>
        <button
          type="submit"
          className={s.saveButton}
            disabled={templeSaving}
            style={{ opacity: templeSaving ? 0.6 : 1, cursor: templeSaving ? 'not-allowed' : 'pointer' }}
          >
            {templeSaving ? (editingTempleId ? 'Updating...' : 'Saving...') : editingTempleId ? 'Update' : 'Save'}
          </button>
          {editingTempleId && (
            <button
              type="button"
              className={s.secondaryButton}
              onClick={() => {
                setEditingTempleId(null)
                setTempleForm(defaultTempleForm)
              }}
            >
              Cancel Edit
        </button>
          )}
        </div>
      </form>

      <div className={s.listSection}>
        <div className={s.listHeader}>
          <h3 className={s.sectionTitle}>Existing Temple Streams</h3>
          <span className={s.countBadge}>{templeStreams.length} streams</span>
        </div>

        {templeStreams.length === 0 ? (
          <div className={s.emptyCard}>
            <NoData />
            <p>No temple live streams have been added yet.</p>
          </div>
        ) : (
          <div className={s.streamList}>
            {templeStreams.map(stream => (
              <article key={stream._id} className={s.streamCard}>
                <div className={s.streamCardTop}>
                  <span className={s.streamBadge}>Live stream</span>
                  <h4>{stream.name}</h4>
                </div>
                <p className={s.streamDetails}>{stream.details}</p>
                <a
                  href={stream.streamUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.streamLink}
                >
                  <span className="material-icons-outlined">play_circle</span>
                  Watch live
                </a>
                <p className={s.dateText}>
                  Added {stream.createdAt ? new Date(stream.createdAt).toLocaleString() : 'recently'}
                </p>
                <div className={s.cardActions}>
                  <button type="button" className={s.updateButton} onClick={() => handleTempleEdit(stream)}>
                    Edit
                  </button>
                  <button type="button" className={s.deleteButton} onClick={() => handleTempleDelete(stream)}>
                    Delete
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className={s.page}>
      <Layouts.Classic title="Spirituality" />

      <div className={s.hero}>
        <p className={s.heroText}>
          Manage spirituality content across purohit registrations, mandir records, and live temple streams.
        </p>

        <div className={s.statsRow}>
          <div className={s.statCard}>
            <span className={s.statLabel}>Purohits</span>
            <strong className={s.statValue}>{stats.purohits}</strong>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Mandirs</span>
            <strong className={s.statValue}>{stats.mandirs}</strong>
          </div>
          <div className={s.statCard}>
            <span className={s.statLabel}>Live streams</span>
            <strong className={s.statValue}>{stats.streams}</strong>
          </div>
        </div>
      </div>

      <div className={s.tabList}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`${s.tabButton} ${activeTab === tab.id ? s.tabButtonActive : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="material-icons-outlined">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className={s.activeTabIntro}>
        <span className="material-icons-outlined">{activeTabMeta.icon}</span>
        <p>{activeTabMeta.description}</p>
      </div>

      {activeTab === 'purohit' && renderPurohitTab()}
      {activeTab === 'mandir' && renderMandirTab()}
      {activeTab === 'streams' && renderStreamsTab()}
    </div>
  )
}
